import type {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsFindDownloadFileMediaFileId,
} from '../../client-server-common/types/doppe-action-cta-button-utils';
import type {DoppeActionTypeDownloadFile} from './doppe-action-type-download-file';
import {doppeActionTypeDownloadFile} from './doppe-action-type-download-file';
import type {DoppeActionTypeDownloadFilePreview} from '../doppe-action-type-download-file-preview/doppe-action-type-download-file-preview';
import {doppeActionTypeDownloadFilePreview} from '../doppe-action-type-download-file-preview/doppe-action-type-download-file-preview';

/**
 * Resolves the Wix Media file id bound to a download-capable action.
 *
 * Three sources are considered, in order:
 *
 * 1. The top-level `doppeActionTypeDownloadFile` action — file id lives
 *    directly on `action.settings.fileId`.
 * 2. The `doppeActionTypeDownloadFilePreview` content-widget action — file
 *    id lives inside `action.settings.fileSettings` (all-or-nothing
 *    `{fileId, fileName}` struct that is `null` when no file is selected).
 *    The seeded `DownloadMainFile` CTA reads from this struct at click time
 *    rather than carrying its own copy.
 * 3. Any `DoppeActionCTAType.DownloadFile` CTA button nested inside another
 *    action's `ctaButtons` (the legacy CTA-option case) — handled by
 *    `doppeActionCtaButtonsFindDownloadFileMediaFileId`.
 *
 * Returns `null` when the action has no download binding. Used by the
 * server-side download endpoints to enforce the file-id <-> action binding
 * (so callers can't substitute an arbitrary file id), and by the native
 * post-checkout thank-you modal to discover which actions on the page are
 * paid downloads.
 */
export function doppeActionResolveBoundDownloadFileId(
    action: DoppeDtoAction,
): string | null {
    if (action.actionTypeId === doppeActionTypeDownloadFile.id) {
        const settings = action.settings as DoppeActionTypeDownloadFile.ActionSettings;
        return settings.fileId || null;
    }
    if (action.actionTypeId === doppeActionTypeDownloadFilePreview.id) {
        const settings = action.settings as DoppeActionTypeDownloadFilePreview.ActionSettings;
        return settings.fileSettings?.fileId ?? null;
    }
    return doppeActionCtaButtonsFindDownloadFileMediaFileId(action);
}
